import {AddressSuggestion, AddressValidationOutcome, AddressValidationResult, ValidationAddress} from "./AddressValidation";

/** Lifecycle of the validation request itself -- NOT the verdict it returns. */
export type AddressValidationRequestStatus = 'NOT_REQUESTED' | 'PENDING' | 'COMPLETE' | 'ERROR';
import {SimpleOrder} from "../app/component/csv/csv-page/SimpleOrder";


export class AddressValidationOrder {
  email: string;
  telephone: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  city: string;
  postalCode: string;
  region: string;
  country: string;
  title: string;
  firstName: string;
  lastName: string;
  company: string;
  shippingInfo: string;
  verifiedAddress1: string;
  verifiedAddress2: string;
  verifiedCity: string;
  verifiedRegion: string;
  verifiedPostalCode: string;
  verifiedCountry: string;
  editedAddress1: string;
  editedAddress2: string;
  editedCity: string;
  editedPostalCode: string;
  editedRegion: string;
  editedCountry: string;

  // Operator overrides for the recipient. Incoming CSV data regularly carries
  // a person's name or a company name in the wrong column (buried in
  // address1/address2, or a company sitting in the name column), so the review
  // step has to be able to move that text into the right field.
  editedTitle: string;
  editedFirstName: string;
  editedLastName: string;
  editedCompany: string;

  /**
   * True once the operator has overridden the imported recipient details.
   *
   * The override deliberately applies no matter which address option is
   * finally used (ORIGINAL / VERIFIED / EDITED). The recipient is not part of
   * the address choice — a name rescued out of a mis-keyed address column must
   * survive the operator then accepting a validated street address, which is
   * the normal flow when the name was stuffed into address1 in the first place.
   */
  nameEdited: boolean = false;

  edit: boolean = false;

  isDirty: boolean = false;

  manualAddressValidationStatus: string = '';
  addressToUse = 'ORIGINAL';  // Also 'VERIFIED' and 'EDITED'

  verifiedStatus: string;
  verifiedResponse: string;
  public addressValidationResult: AddressValidationResult;
  verifiedAddressSetByUser: boolean = false;

  // ---------------------------------------------------------------------------
  // Validation lifecycle
  //
  // Two different facts, deliberately stored separately:
  //
  //   addressValidationRequestStatus   what happened to the REQUEST
  //   addressValidationResult          what the validator SAID, if it answered
  //
  // Collapsing them is what produced the reported behaviour. With only the
  // result to go on, "not asked yet", "asked and still waiting", "asked and the
  // call failed" and "asked, answered, and the address is not real" all present
  // as a missing/empty result, so the screen showed nothing in every case and
  // an operator could not tell a broken validator from a bad address.
  // ---------------------------------------------------------------------------

  /**
   * NOT_REQUESTED -> PENDING -> COMPLETE | ERROR.
   *
   * ERROR here means the call did not complete. It says nothing about the
   * address, which is exactly why it must not be rendered as a verdict.
   */
  addressValidationRequestStatus: AddressValidationRequestStatus = 'NOT_REQUESTED';

  /** Populated only when `addressValidationRequestStatus` is `ERROR`. */
  addressValidationErrorMessage: string = '';

  /**
   * The operator has taken responsibility for an address the validator would
   * not confirm.
   *
   * This exists because the validator is not the last word on whether an
   * address is real. UPS returns no candidates for addresses that Google Maps
   * locates without difficulty -- new construction, recently renumbered
   * streets, rural routes -- and holding those orders forever is not a defence
   * against anything. Someone who can see the address is real needs a way to
   * say so.
   *
   * Set only through `acceptUnvalidatedAddress()`, only offered by the UI to an
   * operator holding the network role, and cleared whenever a fresh validation
   * is requested so the decision is always about the address currently on
   * screen.
   */
  unvalidatedAddressAcceptedByUser: boolean = false;

  /**
   * The recommended addresses, with their comparison against the submitted
   * address already computed. Rebuilt only when a result arrives -- see
   * `AddressSuggestion` for why this is not done in the template.
   *
   * Empty for `UNSUPPORTED_COUNTRY`: the server echoes the address you just
   * sent it back in `recommendedAddresses` for that outcome, so rendering the
   * array would offer the operator their own unvalidated address dressed up as
   * a validator recommendation.
   */
  suggestions: AddressSuggestion[] = [];




  setVerifiedAddressOption (address: ValidationAddress, setByUser: boolean) {
    this.verifiedAddress1 = address.address1;
    this.verifiedAddress2 = address.address2;
    this.verifiedCity = address.city;
    this.verifiedRegion = address.state;
    this.verifiedPostalCode = address.postalcode;
    this.verifiedCountry = address.country;
    this.verifiedAddressSetByUser = setByUser;
    if (setByUser) {
      this.useVerifiedAddress();
    }
    this.isDirty = true;
  }

  setAddressValidationResult (addressValidationResult: AddressValidationResult) {
    this.addressValidationResult = addressValidationResult;
    // A result whose own outcome is ERROR is a failed CALL wearing a result's
    // clothes -- the service builds one so callers never see `undefined`. Route
    // it to the request-failure path so it is not mistaken for a verdict.
    if ( addressValidationResult && addressValidationResult.getOutcome() === 'ERROR' ) {
      this.setAddressValidationError(addressValidationResult.errorMessage);
      return;
    }
    this.addressValidationRequestStatus = 'COMPLETE';
    this.addressValidationErrorMessage = '';
    this.rebuildSuggestions();
    if (this.getAddressValidationStatus() === 'VALID' && this.addressValidationResult.recommendedAddresses.length > 0) {
      const originalAddress1 = this.address1 ? this.address1 : '';
      const firstValidatedAddress1 = this.addressValidationResult.recommendedAddresses[0].address1 ? this.addressValidationResult.recommendedAddresses[0].address1 : '';
      const originalAddress2 = this.address2 ? this.address2 : '';
      const firstValidatedAddress2 = this.addressValidationResult.recommendedAddresses[0].address2 ? this.addressValidationResult.recommendedAddresses[0].address2 : '';
      const originalNumericEnd = this.getNumberAtEndOfString(originalAddress1 + originalAddress2);
      const firstValidatedNumericEnd = this.getNumberAtEndOfString(firstValidatedAddress1 + firstValidatedAddress2);
      if ( originalNumericEnd === firstValidatedNumericEnd && firstValidatedAddress2.indexOf('-') === -1) {
        this.setVerifiedAddressOption(this.addressValidationResult.recommendedAddresses[0], false);
        this.useVerifiedAddress();
      }
    }
    this.isDirty = true;
  }


  /**
   * Rebuild `suggestions` from the current result.
   *
   * Called only when the result changes -- never from a template. That is the
   * whole point: doing this work per change-detection pass is what produced the
   * address that grew a line every fifteen seconds. See `AddressSuggestion`.
   */
  private rebuildSuggestions () {
    this.suggestions = [];
    if ( !this.addressValidationResult || !this.addressValidationResult.recommendedAddresses ) {
      return;
    }
    // See the `suggestions` property comment: for this outcome the array is
    // the address we just submitted, echoed back by the server.
    if ( this.getAddressValidationOutcome() === 'UNSUPPORTED_COUNTRY' ) {
      return;
    }
    for ( const address of this.addressValidationResult.recommendedAddresses ) {
      this.suggestions.push( AddressSuggestion.build(address, this) );
    }
  }

  getNumberAtEndOfString(value: string): string {
    const matches = value.match(/\d+$/);
    if ( matches ) {
      return matches[0];
    }
    return '';
  }

  /**
   * @deprecated Unused since the suggestion list began carrying its own
   * `hasRangeCharacter` per candidate (see `AddressSuggestion`). This asked
   * about the address already CHOSEN while being used to decide whether to show
   * the button that does the choosing, so one ranged candidate hid the accept
   * button on every other suggestion as well. Kept only because
   * `SimpleOrder` extends this class and external callers are hard to rule out.
   */
  doesAddressHaveARangeCharacter(): boolean {
    if ( this.verifiedAddress1 && this.verifiedAddress1.indexOf('-') >= 0 ) {
      return true;
    }
    if ( this.verifiedAddress2 && this.verifiedAddress2.indexOf('-') >= 0 ) {
      return true;
    }
    return false;
  }
  getAddressValidationStatus (): string {
    if ( this.manualAddressValidationStatus === 'INVALID') {
      return 'INVALID';
    }
    if ( this.addressValidationResult && this.addressValidationResult.validationResult === 'Success' && this.addressValidationResult.recommendedAddresses.length === 1 ) {
      return 'VALID';
    } else {
      if ( this.verifiedAddress1 ) {
        return 'VALID';
      } else {
        return 'INVALID';
      }
    }
  }

  toggleMannualAddressValidation () {
    if ( this.manualAddressValidationStatus === 'INVALID' ) {
      this.manualAddressValidationStatus = '';
    } else {
      this.manualAddressValidationStatus = 'INVALID';
    }
    this.isDirty = true;
  }

  useEditedAddress() {
    this.addressToUse = 'EDITED';
    this.isDirty = true;
  }

  useOriginalAddress() {
    this.addressToUse = 'ORIGINAL';
    this.isDirty = true;
  }

  useVerifiedAddress() {
    this.addressToUse = 'VERIFIED';
    this.isDirty = true;
  }

  /**
   * Seeds the edit form from the imported values and opens it. Replaces the
   * long inline field-copying that used to live in the template's click
   * handler. An in-progress recipient override is NOT re-seeded, so reopening
   * the form doesn't silently throw the operator's corrections away.
   */
  startEdit() {
    this.editedAddress1 = this.address1;
    this.editedAddress2 = this.address2;
    this.editedCity = this.city;
    this.editedPostalCode = this.postalCode;
    this.editedRegion = this.region;
    this.editedCountry = this.country;
    if ( !this.nameEdited ) {
      this.editedTitle = this.title;
      this.editedFirstName = this.firstName;
      this.editedLastName = this.lastName;
      this.editedCompany = this.company;
    }
    this.edit = true;
  }


  /**
   * Coerce every text field to an actual string.
   *
   * Seeding code reads from `bookorder.originalAddress`, which is raw JSON
   * with no defaults, so any field the imported order did not carry arrives as
   * `undefined` -- `address2` and `company` most often. Those undefineds used
   * to reach the template, where the address-comparison helper dereferenced
   * them and threw DURING CHANGE DETECTION, leaving half-rendered DOM that the
   * next tick rebuilt on top of. See `AddressSuggestion` for the full story.
   *
   * Both ends are now defended, deliberately: this normalises on the way in,
   * and `Util.getSameAndDifferentFragments` coerces on the way out. Either
   * alone would have prevented the bug; the pair means a new seeding path
   * cannot reintroduce it.
   */
  normalizeStrings () {
    const fields = [
      'email', 'telephone', 'address1', 'address2', 'address3', 'address4',
      'city', 'postalCode', 'region', 'country', 'title', 'firstName',
      'lastName', 'company', 'shippingInfo',
      'verifiedAddress1', 'verifiedAddress2', 'verifiedCity', 'verifiedRegion',
      'verifiedPostalCode', 'verifiedCountry',
      'editedAddress1', 'editedAddress2', 'editedCity', 'editedPostalCode',
      'editedRegion', 'editedCountry',
      'editedTitle', 'editedFirstName', 'editedLastName', 'editedCompany',
    ];
    for ( const field of fields ) {
      const value = this[field];
      if ( value === undefined || value === null ) {
        this[field] = '';
      } else if ( typeof value !== 'string' ) {
        this[field] = String(value);
      }
    }
  }

  markNameEdited() {
    this.nameEdited = true;
    this.isDirty = true;
  }

  /** Drops the recipient override and goes back to what the CSV supplied. */
  revertName() {
    this.editedTitle = this.title;
    this.editedFirstName = this.firstName;
    this.editedLastName = this.lastName;
    this.editedCompany = this.company;
    this.nameEdited = false;
    this.isDirty = true;
  }


  /**
   * Called as the request goes out. Clears the previous verdict on purpose: a
   * stale "valid" badge sitting next to a spinner for a freshly-edited address
   * is worse than no badge, because it is the answer to a question nobody asked
   * any more.
   */
  markAddressValidationRequested () {
    this.addressValidationRequestStatus = 'PENDING';
    this.addressValidationErrorMessage = '';
    this.addressValidationResult = undefined;
    this.suggestions = [];
    // An acceptance is a decision about one specific address and one specific
    // verdict. Asking the validator again replaces the verdict, so the
    // acceptance has to go with it -- otherwise an operator who overrode a
    // rejection, then edited the address and re-validated, would still be
    // carrying the earlier "I take responsibility" into a result they have not
    // seen.
    this.unvalidatedAddressAcceptedByUser = false;
  }

  /**
   * The call did not complete. Recorded as a REQUEST failure, never as a result,
   * so `getAddressValidationOutcome()` reports ERROR rather than manufacturing
   * an empty suggestion list that reads as "not a real address".
   */
  setAddressValidationError (message: string) {
    this.addressValidationRequestStatus = 'ERROR';
    this.addressValidationErrorMessage = message ? message : '';
    this.addressValidationResult = undefined;
    this.suggestions = [];
    this.isDirty = true;
  }

  /**
   * The state to drive the UI from: request lifecycle first, then the
   * validator's own verdict.
   */
  getAddressValidationOutcome (): AddressValidationOutcome {
    if ( this.addressValidationRequestStatus === 'PENDING' ) {
      return 'PENDING';
    }
    if ( this.addressValidationRequestStatus === 'ERROR' ) {
      return 'ERROR';
    }
    if ( !this.addressValidationResult ) {
      return 'NOT_REQUESTED';
    }
    return this.addressValidationResult.getOutcome();
  }

  isAddressValidationPending (): boolean {
    return this.getAddressValidationOutcome() === 'PENDING';
  }

  isAddressValidationInError (): boolean {
    return this.getAddressValidationOutcome() === 'ERROR';
  }

  /**
   * The validator has no coverage for this country -- in practice an
   * international address. The address is unjudged and always will be, so this
   * is the ONE case in which shipping an unvalidated address is a decision
   * rather than an evasion.
   */
  isAddressValidationUnavailableForCountry (): boolean {
    return this.getAddressValidationOutcome() === 'UNSUPPORTED_COUNTRY';
  }

  /**
   * The validator answered and did not recognise the address. Distinct from
   * ERROR in the way that matters: this is a fact ABOUT THE ADDRESS.
   */
  isAddressNotRecognised (): boolean {
    return this.getAddressValidationOutcome() === 'NOT_FOUND';
  }

  /**
   * True when the address that will actually be shipped to is one the validator
   * confirmed -- either accepted from its suggestions or matched exactly.
   *
   * Gated on `addressToUse` as well as on `verifiedAddress1` being present,
   * because a suggestion that was offered and then NOT taken is not a validated
   * address, and `verifiedAddress1` survives the operator switching back to the
   * original or edited address.
   */
  isSelectedAddressValidated (): boolean {
    if ( this.manualAddressValidationStatus === 'INVALID' ) {
      return false;
    }
    return this.addressToUse === 'VERIFIED' && !!this.verifiedAddress1;
  }

  /**
   * The gate: may this address be saved and the order processed with it?
   *
   * Validated addresses pass. Unvalidated ones pass only where validation was
   * never available -- an international address the system cannot check. Every
   * other unvalidated address is held back, which is the rule the operator
   * asked for: not recognised, not shipped.
   *
   * An address the operator has explicitly flagged as bad never passes, whatever
   * the validator said.
   */
  canUseSelectedAddress (): boolean {
    if ( this.manualAddressValidationStatus === 'INVALID' ) {
      return false;
    }
    if ( this.isSelectedAddressValidated() ) {
      return true;
    }
    if ( this.isAddressValidationUnavailableForCountry() ) {
      return true;
    }
    // The operator override. Gated in the UI on the network role, and only
    // offered once validation has actually been attempted -- see
    // canOverrideBeOffered().
    return this.unvalidatedAddressAcceptedByUser && this.canOverrideBeOffered();
  }


  /**
   * Is an override even a sensible thing to offer here?
   *
   * Offered once validation has been ATTEMPTED and the address that would
   * actually ship is still not one the validator stands behind. Deliberately
   * not offered for NOT_REQUESTED or PENDING: "I could not validate this" is a
   * statement about a validation that happened, and an override available
   * before asking would quietly become the normal way to save an address.
   *
   *   NOT_FOUND     the validator ran and recognised nothing. The case this was
   *                 built for -- an address that is real but not in UPS's table
   *   ERROR         the validator could not be reached, or refused the
   *                 credential. Nothing is known about the address. Without an
   *                 override, a validator outage stops all order processing
   *   SUGGESTIONS   candidates came back and the operator does not want any of
   *                 them. This was missing at first, and it is not a rare case:
   *                 UPS answers a rural highway address with a differently
   *                 formatted one, or the wrong one entirely, and an operator
   *                 who has confirmed the original elsewhere had no way to keep
   *                 it -- the gate said "a suggestion was offered, take it"
   *                 while the only offer on the table was wrong
   *   EXACT_MATCH   the validator confirmed an address but the operator has
   *                 selected a different one. Rare, and admitted for the same
   *                 reason as SUGGESTIONS
   *
   * UNSUPPORTED_COUNTRY is absent on purpose: it already proceeds for everyone
   * without an override, because there is no validator to overrule.
   */
  canOverrideBeOffered (): boolean {
    if ( this.manualAddressValidationStatus === 'INVALID' ) {
      return false;
    }
    // Nothing to override -- the address being used is validator-confirmed.
    if ( this.isSelectedAddressValidated() ) {
      return false;
    }
    const outcome = this.getAddressValidationOutcome();
    if ( outcome === 'NOT_REQUESTED' || outcome === 'PENDING' ) {
      return false;
    }
    if ( outcome === 'UNSUPPORTED_COUNTRY' ) {
      return false;
    }
    return true;
  }

  /**
   * Record that the operator is taking responsibility for this address.
   *
   * The caller is responsible for having established that the operator may do
   * this -- see `StartupService.canOverrideAddressValidation()`. This model has
   * no access to the session and deliberately does not try to get one; it
   * records a decision, it does not authorise it.
   */
  acceptUnvalidatedAddress () {
    this.unvalidatedAddressAcceptedByUser = true;
    this.isDirty = true;
  }

  /** Withdraw the acceptance, putting the address back behind the gate. */
  clearUnvalidatedAddressAcceptance () {
    this.unvalidatedAddressAcceptedByUser = false;
    this.isDirty = true;
  }

  /**
   * True when the address about to be used has NOT been confirmed by the
   * validator -- an unvalidatable country, or an operator override.
   *
   * This is what decides `MANUAL_OVERRIDE` versus `SUCCESS` on the
   * EcommerceOrder: both are shippable server-side, but only one of them claims
   * a validator stood behind the address.
   */
  requiresManualOverride (): boolean {
    if ( !this.canUseSelectedAddress() ) {
      return false;
    }
    return !this.isSelectedAddressValidated();
  }

  /**
   * Why `canUseSelectedAddress()` said no, for display. Returns '' when it said
   * yes.
   */
  getBlockingReason (): string {
    if ( this.manualAddressValidationStatus === 'INVALID' ) {
      return 'MARKED_INVALID';
    }
    if ( this.canUseSelectedAddress() ) {
      return '';
    }
    const outcome = this.getAddressValidationOutcome();
    if ( outcome === 'EXACT_MATCH' || outcome === 'SUGGESTIONS' ) {
      // The validator did its job; the operator simply has not taken one of the
      // addresses it stands behind.
      return 'NOT_ACCEPTED';
    }
    return outcome;
  }

  // The recipient values that will actually be placed on the order, whichever
  // address option is selected. Everything downstream must read these rather
  // than the raw imported fields.
  getTitleToUse(): string {
    const value = this.nameEdited ? this.editedTitle : this.title;
    return value ? value : '';
  }

  getFirstNameToUse(): string {
    const value = this.nameEdited ? this.editedFirstName : this.firstName;
    return value ? value : '';
  }

  getLastNameToUse(): string {
    const value = this.nameEdited ? this.editedLastName : this.lastName;
    return value ? value : '';
  }

  getCompanyToUse(): string {
    const value = this.nameEdited ? this.editedCompany : this.company;
    return value ? value : '';
  }

  /**
   * A shipping label needs an addressee. An order whose first name, last name
   * AND company are all blank cannot be delivered, so the review step flags it.
   */
  isRecipientValid(): boolean {
    return ( this.getFirstNameToUse().trim().length > 0
      || this.getLastNameToUse().trim().length > 0
      || this.getCompanyToUse().trim().length > 0 );
  }

}
